// ============================================
// In-game OTA update: first-launch onboarding + update banner (Tauri desktop)
// ============================================
// Replaces the old native OS dialog. On first launch it asks once whether to
// auto-update (and, on a Patreon build, nudges to connect). Thereafter, if
// auto-update is on, it polls the shell's GET /__pno_update and shows an
// on-brand in-game banner; "Update now" POSTs /__pno_update/start and the
// banner shows download progress + a "restart to apply" note. A Settings toggle
// flips auto-update later. No-op outside the Tauri shell.

import { L } from '../systems/localization.js';
import { isPat } from '../engine/pgate.js';
import { pairWithCode } from './entitlement.js';

const LOCAL = 'http://localhost:1430';
const BAR_ID = 'pno-update-bar';
const ONBOARD_ID = 'pno-onboard-bar';

function isTauri() {
    try { return !!(window.electronAPI && window.electronAPI.shell === 'tauri'); }
    catch (e) { return false; }
}
function t(key, en) {
    try { const v = L.get(key); return (v && v !== key) ? v : en; }
    catch (e) { return en; }
}
function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
function autoUpdateOn() { return ls('PnO_AutoUpdate') === 'on'; }

let _phase = 'none';   // update-banner phase: none|offered|downloading|installed|error|dismissed
let _polling = false;
let _timer = null;     // the single pending poll (recursive setTimeout, never setInterval)
let _fastUntil = 0;    // poll quickly until this timestamp (launch/pair burst)

export function initDesktopUpdate() {
    if (!isTauri()) return;
    injectSettingsToggle();
    if (ls('PnO_UpdateOnboarded') !== '1') { runOnboarding(); return; }
    if (autoUpdateOn()) startPolling();
}

// ── DOM helpers ─────────────────────────────────────────────────────────────
function el(tag, attrs, ...kids) {
    const n = document.createElement(tag);
    for (const k in (attrs || {})) {
        if (k === 'class') n.className = attrs[k];
        else if (k === 'text') n.textContent = attrs[k];
        else if (k === 'style') n.style.cssText = attrs[k];
        else n.setAttribute(k, attrs[k]);
    }
    kids.forEach(c => n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c));
    return n;
}
function txt(s) { return el('span', { text: s }); }
function mkBtn(label, primary) {
    return el('button', {
        text: label,
        style: 'padding:6px 16px;border-radius:8px;border:none;cursor:pointer;'
            + 'font-family:inherit;font-size:0.82rem;font-weight:700;'
            + (primary ? 'background:#fff;color:#7c3aed;' : 'background:rgba(255,255,255,0.18);color:#fff;'),
    });
}
function topBar(id, gradient) {
    let b = document.getElementById(id);
    if (!b) { b = el('div', { id }); document.body.appendChild(b); }
    b.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483000;'
        + `background:${gradient};color:#fff;padding:9px 16px;display:flex;align-items:center;`
        + 'justify-content:center;gap:14px;flex-wrap:wrap;font-family:Inter,system-ui,sans-serif;'
        + 'font-size:0.9rem;box-shadow:0 3px 16px rgba(0,0,0,0.35)';
    b.innerHTML = '';
    return b;
}
function removeBar(id) { const b = document.getElementById(id); if (b) b.remove(); }

// ── first-launch onboarding (shown once) ────────────────────────────────────
function runOnboarding() {
    const b = topBar(ONBOARD_ID, 'linear-gradient(135deg,#8b5cf6,#a855f7)');
    b.appendChild(txt(t('onboard.notify', 'Notify you here when a new update is available?')));
    const yes = mkBtn(t('onboard.yes', 'Yes'), true);
    yes.onclick = () => { lsSet('PnO_AutoUpdate', 'on'); finishOnboarding(true); };
    const no = mkBtn(t('onboard.no', 'No'), false);
    no.onclick = () => { lsSet('PnO_AutoUpdate', 'off'); finishOnboarding(false); };
    b.appendChild(yes); b.appendChild(no);
}

async function finishOnboarding(on) {
    lsSet('PnO_UpdateOnboarded', '1');
    syncSettingsToggle();
    // On a Patreon build, nudge to connect (updates are gated on a linked account).
    if (on && isPat()) {
        let paired = false;
        try { paired = !!(await (await fetch(`${LOCAL}/__pno_entitlement`, { cache: 'no-store' })).json()).paired; }
        catch (e) {}
        if (!paired) { showConnectNudge(); if (autoUpdateOn()) startPolling(); return; }
    }
    removeBar(ONBOARD_ID);
    if (on) startPolling();
}

function showConnectNudge() {
    const b = topBar(ONBOARD_ID, 'linear-gradient(135deg,#ec4899,#a855f7)');
    b.appendChild(txt(t('onboard.connect', 'Connect your Patreon account to receive updates (Settings has it too).')));
    const connect = el('a', {
        href: 'https://www.pinkedandowned.com/api/patreon/login?flow=app',
        target: '_blank', rel: 'noopener',
        text: t('entitlement.connect_patreon', 'Connect Patreon'),
        style: 'padding:6px 16px;border-radius:8px;background:#fff;color:#a21caf;'
            + 'text-decoration:none;font-weight:700;font-size:0.82rem',
    });
    // Inline code entry so pairing finishes right here after the browser login,
    // instead of forcing a trip to Settings (foundby:owner test 2026-07-05).
    const input = el('input', {
        type: 'text', placeholder: t('entitlement.code_placeholder', 'paste code (e.g. x7k2-9fq4)'),
        autocomplete: 'off', spellcheck: 'false',
        style: 'padding:6px 10px;border-radius:8px;border:none;font-family:monospace;'
            + 'font-size:0.82rem;width:150px',
    });
    const pair = mkBtn(t('entitlement.pair', 'Pair'), true);
    pair.onclick = async () => {
        const code = (input.value || '').trim();
        if (!code) return;
        pair.disabled = true; pair.textContent = t('entitlement.pairing', 'Pairing...');
        try {
            await pairWithCode(code);
            removeBar(ONBOARD_ID);
            startPolling(); // pairing fires pno-paired -> immediate re-check
        } catch (e) {
            pair.disabled = false; pair.textContent = t('entitlement.pair', 'Pair');
            input.style.outline = '2px solid #fecaca';
        }
    };
    const later = mkBtn(t('onboard.later', 'Later'), false);
    later.onclick = () => removeBar(ONBOARD_ID);
    b.appendChild(connect); b.appendChild(input); b.appendChild(pair); b.appendChild(later);
    // If the player instead pairs from the Settings panel, drop the nudge and
    // start polling so the update offer appears without a relaunch.
    try { window.addEventListener('pno-paired', () => { removeBar(ONBOARD_ID); startPolling(); }, { once: true }); } catch (e) {}
}

// ── Settings toggle (change auto-update later) ──────────────────────────────
function injectSettingsToggle() {
    const host = document.getElementById('menu-settings');
    if (!host || document.getElementById('pno-autoupdate-row')) return;
    const row = el('div', { id: 'pno-autoupdate-row', class: 'settings-row',
        style: 'margin-top:8px;align-items:center;gap:10px' });
    row.appendChild(el('span', { class: 'settings-label',
        text: t('settings.updatenotify', 'Update notifications') }));
    const cb = el('input', { type: 'checkbox', id: 'pno-autoupdate-cb' });
    cb.checked = autoUpdateOn();
    cb.onchange = () => {
        lsSet('PnO_AutoUpdate', cb.checked ? 'on' : 'off');
        lsSet('PnO_UpdateOnboarded', '1');
        if (cb.checked) {
            if (_phase === 'dismissed') _phase = 'none'; // allow a fresh offer
            startPolling();
        } else { removeBar(BAR_ID); _phase = 'dismissed'; }
    };
    row.appendChild(cb);
    host.appendChild(row);
}
function syncSettingsToggle() {
    const cb = document.getElementById('pno-autoupdate-cb');
    if (cb) cb.checked = autoUpdateOn();
}

// ── update banner ────────────────────────────────────────────────────────────
// Exactly ONE pending poll exists at any time — kick() bursts and the fast
// download cadence reschedule the same timer instead of stacking parallel
// chains (pre-fix: the 8s interval spawned a new 700ms self-chain every tick).
function schedule(ms) {
    if (_timer) clearTimeout(_timer);
    _timer = setTimeout(() => { _timer = null; poll(); }, ms);
}

// Poll quickly for ~8s (the shell's background check lands shortly after
// launch/pairing), then settle to the slow cadence.
function kick() { _fastUntil = Date.now() + 8000; schedule(400); }

function startPolling() {
    if (_polling) { kick(); return; }
    _polling = true;
    kick();
    try { window.addEventListener('pno-paired', kick); } catch (e) {}
}

async function poll() {
    if (!autoUpdateOn()) return; // chain parks; the Settings toggle re-arms it
    let j;
    try { j = await (await fetch(`${LOCAL}/__pno_update`, { cache: 'no-store' })).json(); }
    catch (e) { schedule(8000); return; }
    if (j.status === 'installed') {
        if (_phase !== 'installed') render('installed');
        return; // terminal until the player restarts, stop polling
    }
    if (j.status === 'error') {
        if (_phase === 'downloading') render('error');
        // After a dismissed failure, offer the update again — the shell
        // accepts a retry (start_download proceeds from the error state).
        else if (j.available && _phase === 'none') render('offered', j);
        schedule(8000);
        return;
    }
    if (j.status === 'downloading') { render('downloading', j); schedule(700); return; }
    if (j.available && (_phase === 'none' || _phase === 'error')) render('offered', j);
    schedule(Date.now() < _fastUntil ? 900 : 8000);
}

function render(phase, j) {
    if (phase === 'offered') {
        _phase = 'offered';
        const mb = Math.round((j.size || 0) / 1048576);
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#8b5cf6,#a855f7)');
        b.appendChild(txt(t('update.available', 'Update available') + `: v${j.version} (${mb} MB)`));
        const now = mkBtn(t('update.now', 'Update now'), true);
        now.onclick = () => {
            _phase = 'downloading';
            fetch(`${LOCAL}/__pno_update/start`, { method: 'POST' }).catch(() => {});
            render('downloading');
            schedule(700);
        };
        const later = mkBtn(t('update.later', 'Later'), false);
        later.onclick = () => { _phase = 'dismissed'; removeBar(BAR_ID); };
        b.appendChild(now); b.appendChild(later);
    } else if (phase === 'downloading') {
        _phase = 'downloading';
        const total = (j && j.total) ? j.total : 0;
        const prog = (j && j.progress) ? j.progress : 0;
        const pct = total ? Math.min(100, Math.round((prog / total) * 100)) : null;
        const base = t('update.downloading', 'Downloading update');
        const line = pct === null ? base + '...'
            : `${base} ${pct}% (${Math.round(prog / 1048576)} / ${Math.round(total / 1048576)} MB)`;
        // Update in place while the bar exists — a full rebuild every poll
        // resets the fill's width transition and churns the DOM.
        const label = document.getElementById('pno-update-dltext');
        const fill = document.getElementById('pno-update-fill');
        if (label && fill && pct !== null) {
            label.textContent = line;
            fill.style.width = pct + '%';
            return;
        }
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#8b5cf6,#a855f7)');
        if (pct === null) {
            b.appendChild(txt(line));
        } else {
            b.appendChild(el('span', { id: 'pno-update-dltext', text: line }));
            const track = el('div', { style: 'flex:0 0 200px;height:7px;border-radius:7px;'
                + 'background:rgba(255,255,255,0.25);overflow:hidden' });
            track.appendChild(el('div', { id: 'pno-update-fill', style: `height:100%;width:${pct}%;background:#fff;`
                + 'border-radius:7px;transition:width 0.25s' }));
            b.appendChild(track);
        }
    } else if (phase === 'installed') {
        _phase = 'installed';
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#16a34a,#22c55e)');
        b.appendChild(txt(t('update.installed', 'Update installed. Close and reopen the game to apply it.')));
    } else if (phase === 'error') {
        _phase = 'error';
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#dc2626,#ef4444)');
        b.appendChild(txt(t('update.error', 'Update failed. The game will try again next time.')));
        const dismiss = mkBtn(t('update.dismiss', 'Dismiss'), false);
        dismiss.onclick = () => { _phase = 'none'; removeBar(BAR_ID); };
        b.appendChild(dismiss);
    }
}
